import React, { useState } from 'react';
import { Button, Input, message, Space } from 'antd';
import { RobotOutlined, UserOutlined, TeamOutlined, StopOutlined } from '@ant-design/icons';

import {
  ControlsWrapper,
  CheckboxSection,
  ReviewHeader,
  SubmitSection,
  CommentInputWrapper,
  StyledCheckbox,
} from './ReviewControls.styled';

export enum ReviewChoice {
  ACCEPT_LEFT = 'ACCEPT_LEFT',
  ACCEPT_RIGHT = 'ACCEPT_RIGHT',
  ACCEPT_BOTH = 'ACCEPT_BOTH',
  ACCEPT_NEITHER = 'ACCEPT_NEITHER',
}

interface ReviewControlsProps {
  onReview: (choice: ReviewChoice, comment?: string) => Promise<void>;
  disabled?: boolean;
  existingReview?: {
    choice: ReviewChoice;
    comment?: string;
  };
  shuffledSegmentations?: boolean;
  groundTruthSide?: 'left' | 'right' | null;
  isBlinded?: boolean;
}

const ReviewControls: React.FC<ReviewControlsProps> = ({
  onReview,
  disabled = false,
  existingReview,
  isBlinded = false,
}) => {
  const [comment, setComment] = useState(existingReview?.comment || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedChoice, setSelectedChoice] = useState<ReviewChoice | null>(
    existingReview?.choice || null
  );

  const handleReview = async (choice: ReviewChoice) => {
    try {
      setIsSubmitting(true);
      await onReview(choice, comment);
      setSelectedChoice(choice);
      message.success('Review submitted successfully');
    } catch (error) {
      message.error('Failed to submit review');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChoiceChange = (choice: ReviewChoice, checked: boolean) => {
    if (checked) {
      setSelectedChoice(choice);
    } else if (selectedChoice === choice) {
      setSelectedChoice(null);
    }
  };

  const handleSubmit = async () => {
    if (!selectedChoice) {
      message.warning('Please select a review choice first');
      return;
    }
    await handleReview(selectedChoice);
  };

  const getCheckboxOptions = () => {
    if (isBlinded) {
      return [
        {
          value: ReviewChoice.ACCEPT_LEFT,
          icon: <UserOutlined />,
          label: 'Accept Segmentation A',
        },
        {
          value: ReviewChoice.ACCEPT_RIGHT,
          icon: <RobotOutlined />,
          label: 'Accept Segmentation B',
        },
        { value: ReviewChoice.ACCEPT_BOTH, icon: <TeamOutlined />, label: 'Accept Both' },
        { value: ReviewChoice.ACCEPT_NEITHER, icon: <StopOutlined />, label: 'Accept Neither' },
      ];
    }

    return [
      { value: ReviewChoice.ACCEPT_LEFT, icon: <UserOutlined />, label: 'Accept Human' },
      { value: ReviewChoice.ACCEPT_RIGHT, icon: <RobotOutlined />, label: 'Accept AI' },
      { value: ReviewChoice.ACCEPT_BOTH, icon: <TeamOutlined />, label: 'Accept Both' },
      { value: ReviewChoice.ACCEPT_NEITHER, icon: <StopOutlined />, label: 'Accept Neither' },
    ];
  };

  const checkboxOptions = getCheckboxOptions();

  return (
    <ControlsWrapper>
      <ReviewHeader>
        <h4>Review Decision</h4>
      </ReviewHeader>

      <CheckboxSection>
        <Space direction="vertical" size={12}>
          {checkboxOptions.map(option => (
            <StyledCheckbox
              key={option.value}
              checked={selectedChoice === option.value}
              onChange={e => handleChoiceChange(option.value, e.target.checked)}
              disabled={disabled || isSubmitting}
            >
              <Space>
                {option.icon}
                {option.label}
              </Space>
            </StyledCheckbox>
          ))}
        </Space>
      </CheckboxSection>

      <CommentInputWrapper>
        <Input.TextArea
          value={comment}
          onChange={e => setComment(e.target.value)}
          placeholder="Add a comment (optional)..."
          rows={2}
          style={{ resize: 'none' }}
        />
      </CommentInputWrapper>

      <SubmitSection>
        <Button
          type="primary"
          onClick={handleSubmit}
          disabled={!selectedChoice || disabled || isSubmitting}
          loading={isSubmitting}
        >
          Submit Review
        </Button>
      </SubmitSection>
    </ControlsWrapper>
  );
};

export default ReviewControls;
